import { RefabricProURL } from "../index";
import { ApiResponse } from "../HttpClient";
import { ModelGender } from "api/models/models.type";
import serializeParams from "api/serializeParams";
import {
  IUtilsImageRemoveBgRequest,
  IUtilsImageDescribeRequest,
  IUtilsUploadPatternRequest,
  IUtilsContactUsRequest,
  IUtilsUploadFilesRequest,
  IUtilsBatchUploadRequest,
  IUtilsBatchUploadResponse,
  IUtilsGenerateUploadUrlResponse,
  IUtilsGenerateResumableUploadUrlRequest,
  IUtilsGenerateResumableUploadUrlResponse,
  IUtilsGetUploadedFilesParams,
  IUtilsGetUploadedFilesResponse,
  ITempModelPosesResponse,
  IAssetsFiltersResponse,
  IBulkDownloadRequest,
  IBulkDownloadResponse,
  IProcessDownloadRequest,
  IProcessDownloadResponse,
  ILogDownloadRequest,
  IUtilsReuploadImageRequest,
  IUtilsReuploadImageResponse,
  IUtilsReflatImageRequest,
  IProductAngles,
  IProductExtraImages,
  ISvgConvertStatusResponse,
  ISetUploadFileDescriptionResponse,
  IUtilsEditUploadedFileResponse,
  IUtilsSetUploadedFileSlotRequest,
  IUtilsSetUploadedFileSlotResponse,
} from "./utils.type";

/**
 * REF-6194 — does this `extra_images` object carry anything at all?
 *
 * `editUploadedFile` omits the key when it doesn't, which is safe only because the BE stores an empty object
 * as NULL either way. The check MUST cover every bucket: an asset whose detail shots are all tagged Front or
 * Back has only `detail_front` / `detail_back` populated, and a check that looked at `label` / `detail` alone
 * would read falsy, omit the key, and make the BE null the column — silently discarding those images along
 * with the label. See `IProductExtraImages` for the replace-vs-COALESCE asymmetry behind that. REF-7104 added
 * `logo`: a product whose only extra images are its logos must still ship the key.
 */
export const hasAnyExtraImage = (extra?: IProductExtraImages): boolean =>
  !!extra && (!!extra.label || !!extra.detail?.length || !!extra.detail_front?.length || !!extra.detail_back?.length || !!extra.logo?.length);

export default {
  async removeBackground(data: IUtilsImageRemoveBgRequest, options?: any): Promise<ApiResponse<any>> {
    return await RefabricProURL.post(`utils/remove_bg`, data, options);
  },
  async describeImage(data: IUtilsImageDescribeRequest, options?: any): Promise<ApiResponse<any>> {
    return await RefabricProURL.post(`utils/describe`, data, options);
  },
  async uploadPattern(data: IUtilsUploadPatternRequest): Promise<ApiResponse<any>> {
    return await RefabricProURL.post(`utils/upload_pattern`, data, {});
  },
  async getUploadedPatterns(): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`utils/upload_pattern`, {});
  },
  async contactUs(data: IUtilsContactUsRequest): Promise<ApiResponse<any>> {
    return await RefabricProURL.post(`utils/contacts`, data, {});
  },
  async getMagicSelectMasks(image_path: string, options?: any): Promise<ApiResponse<any>> {
    return await RefabricProURL.post(`utils/magic-selection-points`, { image_path: image_path }, options);
  },
  async uploadFiles(data: IUtilsUploadFilesRequest): Promise<ApiResponse<any>> {
    const category = data.category || "design_assets";

    return await RefabricProURL.post(`utils/upload_files`, { ...data, category }, {});
  },
  // Batch register up to 200 already-hosted assets in one atomic request. Lightweight (no thumbnail /
  // orientation / studio detection) — for bulk registration where the URLs are already good.
  async batchUploadFiles(data: IUtilsBatchUploadRequest): Promise<ApiResponse<IUtilsBatchUploadResponse>> {
    return await RefabricProURL.post(`utils/upload_files/batch`, data, {});
  },
  /**
   * Get uploaded files with optional gender filter
   * GET /upload_files?file_type=model&gender=WOMAN&page=1&limit=20
   *
   * Behavior:
   * - If gender is set → only matching gender assets returned
   * - Assets with gender=null are NOT returned when filtering
   * - If gender is omitted → all assets returned
   */
  // `signal` (REF-7048): the caller's AbortController, so a superseded search is CANCELLED on the wire
  // rather than only ignored when it lands — an ignored request still runs its query on the server.
  async getUploadedFiles({ file_type, params, signal }: { file_type: string; params?: IUtilsGetUploadedFilesParams; signal?: AbortSignal }): Promise<ApiResponse<IUtilsGetUploadedFilesResponse>> {
    const queryParams: Record<string, any> = { ...params };

    // Only include gender in params if it's explicitly set (not undefined)
    // null is a valid value that should be passed
    if (params?.gender !== undefined) {
      queryParams.gender = params.gender;
    }

    // REF-6429 — `exclude_types` is a REPEATED BARE KEY (`?exclude_types=background&exclude_types=pose`),
    // which is the only form FastAPI binds to a `list[str] = Query(...)`. Axios 0.26 renders an array as
    // `exclude_types[]=…`, which FastAPI drops as an unknown parameter — and the request still 200s with
    // an UNFILTERED list, so the bug would look like "the fix did nothing".
    //
    // Attached ONLY when a param is actually an array, so every existing caller's query string stays
    // byte-identical: `serializeParams` also drops empty strings, which axios' default serializer sends.
    const hasArrayParam = Object.values(queryParams).some((value) => Array.isArray(value));

    return await RefabricProURL.get(`utils/upload_files/${file_type}`, {
      params: queryParams,
      ...(hasArrayParam ? { paramsSerializer: serializeParams } : {}),
      ...(signal ? { signal } : {}),
    });
  },
  async getUploadedFileById({ assets_id }: { assets_id: string | number }): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`utils/upload_file/${assets_id}`, {});
  },
  async deleteUploadedFile({ assets_id }: { assets_id: string }): Promise<ApiResponse<any>> {
    return await RefabricProURL.delete(`utils/upload_files/${assets_id}`, {});
  },
  // Toggle the per-asset like (owner-only; samples always report false). Empty body → { is_liked }.
  async toggleUploadedFileLike({ assets_id }: { assets_id: string | number }): Promise<ApiResponse<{ is_liked: boolean }>> {
    return await RefabricProURL.post(`utils/upload_files/${assets_id}/like`, {}, {});
  },
  async editUploadedFile({
    assets_id,
    image_path,
    file_type,
    object_desc,
    category,
    angles,
    extra_images,
    view_image_urls,
    gender,
    fit_prompt,
    external_id,
  }: {
    assets_id: string;
    image_path: string;
    file_type: string;
    object_desc: string;
    category?: string;
    angles?: IProductAngles;
    extra_images?: IProductExtraImages;
    // Background angle frames (REF-6157). Contract-ahead — the BE must accept + persist these on edit
    // (see docs/be-handover/background-angles-request.md). Only sent for file_type "background".
    view_image_urls?: string[];
    gender?: ModelGender;
    fit_prompt?: string;
    external_id?: string | number;
  }): Promise<ApiResponse<IUtilsEditUploadedFileResponse>> {
    // REF-5934: external_id is the one field the BE writes with COALESCE(%s, external_id) — omitting
    // it PRESERVES the stored value instead of nulling it (deliberate, so the existing edit callers
    // that don't send it can't wipe an external reference). Only forward it when a caller explicitly
    // passed something, and note there is currently NO way to clear it back to NULL via this endpoint.
    //
    // REF-7063 — `v3Errors`: a 403 here is "not your row", not an expired session, so it must reject to the caller
    // instead of logging the user out (409/503 reject too, without the global snackbar; every caller toasts its own
    // failure). A 404 already rejected. Callers confirm a resolved save with `isUploadedFileEditConfirmed` (./uploadedFileEdit).
    return await RefabricProURL.post(
      `utils/upload_files/${assets_id}`,
      {
        image_path,
        file_type,
        object_desc,
        ...(angles && Object.keys(angles).length > 0 && { angles }),
        ...(hasAnyExtraImage(extra_images) && { extra_images }),
        // BE contract: omitted/undefined = don't touch angles; explicit [] = clear all angles (revert to
        // flat); [url,...] = replace. Gate on "was the arg passed", NOT on truthiness/length, or an explicit
        // [] silently degrades to "don't touch" and the clear-angles action becomes a no-op.
        ...(view_image_urls !== undefined && { view_image_urls }),
        ...(category && { category }),
        ...(gender && { gender }),
        ...(fit_prompt !== undefined && { fit_prompt }),
        ...(external_id !== undefined && { external_id }),
      },
      { v3Errors: true }
    );
  },
  // REF-7063 — write ONE image slot of a ROAR product (any team member who can see it). The only write a ROAR product
  // takes: the generic edit above is owner-only and refuses ROAR-locked slots. `v3Errors` so the 409 "ROAR-locked" and
  // the 404 reach the caller instead of the global snackbar; confirm with `isUploadedFileSlotConfirmed`.
  async setUploadedFileSlot({ assets_id, ...data }: IUtilsSetUploadedFileSlotRequest & { assets_id: string | number }): Promise<ApiResponse<IUtilsSetUploadedFileSlotResponse>> {
    return await RefabricProURL.post(`utils/upload_files/${assets_id}/slot`, data, { v3Errors: true });
  },
  // Write the free-form description (owner-only). POST /v1/utils/upload_files/{assets_id}/description.
  // Max 2000 chars; "" or whitespace-only CLEARS it (reads back as null). Known Trap #1: on a non-owner /
  // sample / missing asset the BE resolves HTTP 200 with `responseCode: 404` (+ `_handled: true`) instead
  // of throwing — callers MUST check `res?._handled` before treating this as a successful save.
  async setUploadFileDescription({ assets_id, description }: { assets_id: string | number; description: string }): Promise<ApiResponse<ISetUploadFileDescriptionResponse>> {
    return await RefabricProURL.post(`utils/upload_files/${assets_id}/description`, { description }, {});
  },
  async generateUploadUrl({ file_name, content_type }: { file_name: string; content_type?: any }): Promise<IUtilsGenerateUploadUrlResponse> {
    return (await RefabricProURL.post<IUtilsGenerateUploadUrlResponse>(`utils/generate_upload_url`, { content_type, file_name })) as unknown as IUtilsGenerateUploadUrlResponse;
  },
  async generateResumableUploadUrl(data: IUtilsGenerateResumableUploadUrlRequest): Promise<IUtilsGenerateResumableUploadUrlResponse> {
    return (await RefabricProURL.post<IUtilsGenerateResumableUploadUrlResponse>(`utils/generate_resumable_upload_url`, data)) as unknown as IUtilsGenerateResumableUploadUrlResponse;
  },
  async addCustomSize(data: { title: string; image_width: number; image_height: number }): Promise<ApiResponse<any>> {
    return await RefabricProURL.post(`utils/image-custom-size`, data, {});
  },
  async getCustomSizes(): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`utils/image-custom-sizes`, {});
  },
  async getTempModels({ type }: { type?: string } = {}): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`utils/temp_models`, { params: { type } });
  },
  async getTempModelPoses(model_id: string): Promise<ApiResponse<ITempModelPosesResponse>> {
    return await RefabricProURL.get(`utils/temp_model_poses`, { params: { model_id } });
  },
  // `svg_style` / `preserve_color` are deliberately not sent (REF-6631): every file_type=svg request
  // runs the AI engine, which ignores both, so the old style/color picker had nothing to choose.
  async downloadTechPack({ img_url, file_type }: { img_url: string; file_type: string }): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`utils/download_tech_pack`, { params: { img_url, file_type } });
  },
  /**
   * Poll one Astra SVG conversion job (BE `astra-svg-default-pipeline`).
   *
   * The path is built from `job_id` and NOT from the 202 body's `status_url`: `RefabricProURL`'s base
   * is `REACT_APP_API_URL + "/" + REACT_APP_API_VERSION`, so it already ends in `/v1` and replaying
   * `/v1/utils/svg_convert_status/…` verbatim would request `/v1/v1/utils/…`.
   *
   * 404 = the job is missing OR belongs to another user (the BE deliberately does not distinguish the
   * two) and is terminal — the caller must stop polling rather than retry to its deadline. Per
   * `.claude/rules/api-services.md` that rejection is the caller's to handle; nothing is caught here.
   *
   * Polling does not touch the download counter: the `download_logs` row is written once, on the
   * initiating `download_tech_pack` request, and never on this endpoint.
   */
  async getSvgConvertStatus(job_id: string): Promise<ApiResponse<ISvgConvertStatusResponse>> {
    return await RefabricProURL.get(`utils/svg_convert_status/${encodeURIComponent(job_id)}`, {});
  },
  async getAssetsFilters(): Promise<ApiResponse<IAssetsFiltersResponse>> {
    return await RefabricProURL.get(`utils/assets_filters`, {});
  },
  async bulkDownload(data: IBulkDownloadRequest): Promise<ApiResponse<IBulkDownloadResponse>> {
    return await RefabricProURL.post(`utils/bulk_download`, data, {});
  },
  // REF-5701: process an image server-side for download (burn "AI Generated" pill + embed
  // AI-provenance metadata + re-encode to file_type). Returns { download_url } inside SuccessDefault.
  async processDownload(data: IProcessDownloadRequest): Promise<ApiResponse<IProcessDownloadResponse>> {
    return await RefabricProURL.post(`utils/process-download`, data, {});
  },
  async reuploadImage(data: IUtilsReuploadImageRequest): Promise<ApiResponse<IUtilsReuploadImageResponse>> {
    return await RefabricProURL.post("utils/reupload_image", data, {});
  },

  async reflatImage(data: IUtilsReflatImageRequest): Promise<ApiResponse<any>> {
    return await RefabricProURL.post("utils/reflat_image", data, {});
  },

  async getReflatStatus(imageId: string): Promise<ApiResponse<any>> {
    return await RefabricProURL.get(`utils/reflat_status/${imageId}`, {});
  },

  // Fire-and-forget download log ping (REF-5664 Option C). Best-effort analytics only: it must
  // NEVER block or fail a download, so errors are swallowed here (the BE endpoint always 200s and
  // swallows DB errors too). Call without awaiting, or await in a try/catch — either is safe.
  async logDownload(data: ILogDownloadRequest): Promise<void> {
    try {
      await RefabricProURL.post("utils/log-download", data, {});
    } catch {
      // analytics only — never surface to the user
    }
  },
};
